import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

export default function ApplyPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    propertyType: '',
    propertyValue: '',
    loanAmount: '',
    rentalIncome: '',
    creditScore: '',
    propertyAddress: '',
    city: '',
    state: '',
    zipCode: '',
    timeframe: '',
    experience: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5lf9r144v703eh90r70', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          propertyType: '',
          propertyValue: '',
          loanAmount: '',
          rentalIncome: '',
          creditScore: '',
          propertyAddress: '',
          city: '',
          state: '',
          zipCode: '',
          timeframe: '',
          experience: '',
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateEstimatedDSCR = () => {
    const loan = parseFloat(formData.loanAmount) || 0;
    const income = parseFloat(formData.rentalIncome) || 0;
    if (loan === 0 || income === 0) return 'N/A';
    const monthlyPayment = (loan * 0.07) / 12;
    const dscr = income / monthlyPayment;
    return dscr.toFixed(2);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section - Mobile responsive */}
      <section className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
              Apply for Your DSCR Loan Today
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 max-w-3xl mx-auto">
              Get pre-qualified in minutes. No income verification required. Connect with top DSCR lenders nationwide and receive competitive rate quotes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Bar - Mobile grid 2 columns */}
      <section className="bg-white py-6 sm:py-8 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="flex items-center justify-center sm:justify-start">
              <i className="ri-file-forbid-line text-2xl sm:text-3xl text-blue-600 mr-3 flex-shrink-0"></i>
              <div>
                <p className="font-bold text-slate-900 text-sm sm:text-base">No W2 Required</p>
                <p className="text-xs sm:text-sm text-slate-600">No income verification</p>
              </div>
            </div>
            <div className="flex items-center justify-center sm:justify-start">
              <i className="ri-speed-up-line text-2xl sm:text-3xl text-blue-600 mr-3 flex-shrink-0"></i>
              <div>
                <p className="font-bold text-slate-900 text-sm sm:text-base">Fast Approval</p>
                <p className="text-xs sm:text-sm text-slate-600">Close in 2-3 weeks</p>
              </div>
            </div>
            <div className="flex items-center justify-center sm:justify-start">
              <i className="ri-percent-line text-2xl sm:text-3xl text-amber-600 mr-3 flex-shrink-0"></i>
              <div>
                <p className="font-bold text-slate-900 text-sm sm:text-base">Competitive Rates</p>
                <p className="text-xs sm:text-sm text-slate-600">Starting at 6.99%</p>
              </div>
            </div>
            <div className="flex items-center justify-center sm:justify-start">
              <i className="ri-infinity-line text-2xl sm:text-3xl text-amber-600 mr-3 flex-shrink-0"></i>
              <div>
                <p className="font-bold text-slate-900 text-sm sm:text-base">Unlimited Properties</p>
                <p className="text-xs sm:text-sm text-slate-600">No portfolio limits</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form - Mobile optimized */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 md:p-12 border border-slate-200">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2 text-center">
                DSCR Loan Pre-Qualification Application
              </h2>
              <p className="text-sm sm:text-base text-slate-600 text-center mb-6 sm:mb-8">
                Complete the form below to receive personalized rate quotes from multiple lenders
              </p>

              <form id="dscr-application-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                {/* Personal Information */}
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 sm:mb-4 flex items-center">
                    <i className="ri-user-line text-emerald-600 mr-2"></i>
                    Personal Information
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[48px]"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[48px]"
                        placeholder="Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[48px]"
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[48px]"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                {/* Property Information */}
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 sm:mb-4 flex items-center">
                    <i className="ri-home-4-line text-emerald-600 mr-2"></i>
                    Property Information
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Property Type *
                      </label>
                      <select
                        name="propertyType"
                        value={formData.propertyType}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[48px]"
                      >
                        <option value="">Select property type</option>
                        <option value="Single Family">Single Family Home</option>
                        <option value="Multifamily 2-4">Multifamily (2-4 units)</option>
                        <option value="Multifamily 5+">Multifamily (5+ units)</option>
                        <option value="Short-Term Rental">Short-Term Rental / Airbnb</option>
                        <option value="Condo">Condo</option>
                        <option value="Townhouse">Townhouse</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Estimated Property Value *
                      </label>
                      <input
                        type="number"
                        name="propertyValue"
                        value={formData.propertyValue}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[48px]"
                        placeholder="400000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Desired Loan Amount *
                      </label>
                      <input
                        type="number"
                        name="loanAmount"
                        value={formData.loanAmount}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[48px]"
                        placeholder="300000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Monthly Rental Income *
                      </label>
                      <input
                        type="number"
                        name="rentalIncome"
                        value={formData.rentalIncome}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[48px]"
                        placeholder="3000"
                      />
                    </div>
                  </div>

                  {/* DSCR Estimate Display - Mobile optimized */}
                  {formData.loanAmount && formData.rentalIncome && (
                    <div className="mt-4 sm:mt-6 bg-gradient-to-br from-blue-50 to-yellow-50 rounded-xl p-4 border-2 border-blue-200">
                      <div className="flex flex-col xs:flex-row items-center justify-between gap-3">
                        <div className="text-center xs:text-left">
                          <p className="text-xs sm:text-sm text-slate-600 mb-1">Estimated DSCR</p>
                          <p className="text-2xl sm:text-3xl font-bold text-blue-600">{calculateEstimatedDSCR()}</p>
                        </div>
                        <div className="text-center xs:text-right">
                          <p className="text-xs sm:text-sm text-slate-600 mb-1">Qualification Status</p>
                          <p className={`text-base sm:text-lg font-bold ${parseFloat(calculateEstimatedDSCR()) >= 1.0 ? 'text-blue-600' : 'text-yellow-600'}`}>
                            {parseFloat(calculateEstimatedDSCR()) >= 1.25 ? 'Excellent' : parseFloat(calculateEstimatedDSCR()) >= 1.0 ? 'Good' : 'May Qualify'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Property Location */}
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 sm:mb-4 flex items-center">
                    <i className="ri-map-pin-line text-emerald-600 mr-2"></i>
                    Property Location
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Property Address
                      </label>
                      <input
                        type="text"
                        name="propertyAddress"
                        value={formData.propertyAddress}
                        onChange={handleChange}
                        className="w-full px-4 py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[48px]"
                        placeholder="123 Main Street"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[48px]"
                        placeholder="Los Angeles"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        State *
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[48px]"
                        placeholder="CA"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[48px]"
                        placeholder="90001"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Details */}
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 sm:mb-4 flex items-center">
                    <i className="ri-information-line text-emerald-600 mr-2"></i>
                    Additional Details
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Credit Score Range *
                      </label>
                      <select
                        name="creditScore"
                        value={formData.creditScore}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[48px]"
                      >
                        <option value="">Select credit score range</option>
                        <option value="740+">740+ (Excellent)</option>
                        <option value="700-739">700-739 (Good)</option>
                        <option value="680-699">680-699 (Fair)</option>
                        <option value="660-679">660-679 (Average)</option>
                        <option value="Below 660">Below 660</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Purchase Timeframe *
                      </label>
                      <select
                        name="timeframe"
                        value={formData.timeframe}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[48px]"
                      >
                        <option value="">Select timeframe</option>
                        <option value="Immediately">Immediately (0-30 days)</option>
                        <option value="1-3 months">1-3 months</option>
                        <option value="3-6 months">3-6 months</option>
                        <option value="6+ months">6+ months</option>
                        <option value="Refinance">Refinancing existing property</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Investment Experience *
                      </label>
                      <select
                        name="experience"
                        value={formData.experience}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[48px]"
                      >
                        <option value="">Select your experience level</option>
                        <option value="First Property">First investment property</option>
                        <option value="2-5 Properties">2-5 properties</option>
                        <option value="6-10 Properties">6-10 properties</option>
                        <option value="10+ Properties">10+ properties</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Additional Information
                      </label>
                      <textarea
                        name="additionalInfo"
                        value={formData.additionalInfo}
                        onChange={handleChange}
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                        placeholder="Tell us more about your investment goals, property details, or any questions you have... (Max 500 characters)"
                      ></textarea>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        {formData.additionalInfo.length}/500 characters
                      </p>
                    </div>
                  </div>
                </div>

                {/* Submit Button - Mobile optimized */}
                <div className="pt-4 sm:pt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed min-h-[52px] whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Submitting Application...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <i className="ri-send-plane-fill mr-2"></i>
                        Get My Free Rate Quotes
                      </span>
                    )}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="mt-4 bg-blue-50 border-2 border-blue-500 rounded-xl p-4">
                      <div className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-blue-600 text-xl sm:text-2xl mr-3 flex-shrink-0 mt-0.5"></i>
                        <div>
                          <p className="font-bold text-blue-900 text-sm sm:text-base">Application Submitted Successfully!</p>
                          <p className="text-xs sm:text-sm text-blue-700 mt-1">A DSCR loan specialist will contact you within 24 hours with personalized rate quotes.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mt-4 bg-red-50 border-2 border-red-500 rounded-xl p-4">
                      <div className="flex items-start">
                        <i className="ri-error-warning-fill text-red-600 text-xl sm:text-2xl mr-3 flex-shrink-0 mt-0.5"></i>
                        <div>
                          <p className="font-bold text-red-900 text-sm sm:text-base">Submission Failed</p>
                          <p className="text-xs sm:text-sm text-red-700 mt-1">Please try again or call us at (800) 555-DSCR for immediate assistance.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <p className="text-xs sm:text-sm text-slate-600 text-center mt-4">
                    By submitting this form, you agree to be contacted by DSCR lenders regarding your loan inquiry.
                  </p>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Apply Section - Mobile grid */}
      <section className="py-12 sm:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8 sm:mb-12 text-center">
            Why Apply for a DSCR Loan?
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-slate-200">
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                <i className="ri-file-forbid-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">No Income Documentation</h3>
              <p className="text-slate-700">
                Apply for DSCR loans without providing W2s, tax returns, or pay stubs. Perfect for self-employed investors and those with complex income situations.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-slate-200">
              <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                <i className="ri-speed-up-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Fast DSCR Loan Approval</h3>
              <p className="text-slate-700">
                Get pre-approved quickly and close in as little as 2-3 weeks. Our streamlined DSCR loan application process means faster access to financing.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-slate-200">
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                <i className="ri-group-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Multiple Lender Quotes</h3>
              <p className="text-slate-700">
                One application connects you with multiple top DSCR lenders nationwide. Compare rates and terms to find the best DSCR loan for your investment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Mobile optimized */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8 sm:mb-12 text-center">
            DSCR Loan Application FAQs
          </h2>
          
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-blue-300 transition-colors">
              <h3 className="font-bold text-slate-900 mb-2 sm:mb-3">How do I apply for a DSCR loan?</h3>
              <p className="text-slate-700">
                To apply for a DSCR loan, complete our online pre-qualification form above. You'll need basic information about yourself, your property, and the rental income. No tax returns or W2s required. A loan specialist will contact you within 24 hours with personalized rate quotes.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-blue-300 transition-colors">
              <h3 className="font-bold text-slate-900 mb-2 sm:mb-3">What documents do I need for a DSCR loan application?</h3>
              <p className="text-slate-700">
                DSCR loan applications require minimal documentation: photo ID, credit authorization, property address, and lease agreement or rent analysis. Unlike traditional mortgages, you don't need to provide tax returns, W2s, pay stubs, or employment verification.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-blue-300 transition-colors">
              <h3 className="font-bold text-slate-900 mb-2 sm:mb-3">How long does DSCR loan approval take?</h3>
              <p className="text-slate-700">
                DSCR loan approval typically takes 3-7 business days after submitting your application. The entire process from application to closing usually takes 2-3 weeks, significantly faster than conventional mortgages that can take 45-60 days.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-blue-300 transition-colors">
              <h3 className="font-bold text-slate-900 mb-2 sm:mb-3">Can I apply for a DSCR loan with bad credit?</h3>
              <p className="text-slate-700">
                Most DSCR lenders require a minimum credit score of 660-680, though some may work with scores as low as 620. While DSCR loans don't require income verification, credit score still matters for rate qualification and approval.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-blue-300 transition-colors">
              <h3 className="font-bold text-slate-900 mb-2 sm:mb-3">What is the minimum DSCR ratio to qualify?</h3>
              <p className="text-slate-700">
                Most lenders require a minimum DSCR of 1.0, meaning the property's rental income covers the mortgage payment. Some lenders offer "no ratio" DSCR loans with ratios as low as 0.75, though these require larger down payments and may have higher rates.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-blue-300 transition-colors">
              <h3 className="font-bold text-slate-900 mb-2 sm:mb-3">Can I apply for multiple DSCR loans at once?</h3>
              <p className="text-slate-700">
                Yes! One of the key benefits of DSCR loans is unlimited property financing. You can apply for DSCR loans on multiple properties simultaneously, with each property evaluated independently based on its own rental income and DSCR ratio.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA - Mobile buttons */}
      <section className="py-12 sm:py-16 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Need Help with Your Application?</h2>
          <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-6 sm:mb-8">
            Our DSCR loan specialists are here to answer your questions and guide you through the application process.
          </p>
          <div className="flex flex-col xs:flex-row gap-3 sm:gap-4 justify-center max-w-md mx-auto">
            <a
              href="tel:8005557327"
              className="inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-base hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl min-h-[48px] whitespace-nowrap"
            >
              <i className="ri-phone-fill mr-2"></i>
              Call (800) 555-DSCR
            </a>
            <a
              href="mailto:info@dscrloans.com"
              className="inline-flex items-center justify-center bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-base hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg hover:shadow-xl border-2 border-white/20 min-h-[48px] whitespace-nowrap"
            >
              <i className="ri-mail-fill mr-2"></i>
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}